import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const [mode, setMode] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [showDisplayNamePrompt, setShowDisplayNamePrompt] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithEmail, sendPhoneOtp, verifyPhoneOtp, updateDisplayName } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/';

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginWithEmail(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendPhoneOtp(phone);
      setOtpSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await verifyPhoneOtp(phone, otpCode);
      // Check if new user (no display_name in metadata means auto-created via phone)
      const isNew = !data?.user?.user_metadata?.display_name;
      if (isNew) {
        setShowDisplayNamePrompt(true);
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDisplayName = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await updateDisplayName(displayName);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (showDisplayNamePrompt) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <img src="/logo.svg" alt="Emotional Intelligence" style={{ width: 80, height: 80, margin: '0 auto 16px', display: 'block' }} />
          <h1>Welcome!</h1>
          <p className="subtitle">What should we call you?</p>
          {error && <div className="error-msg">{error}</div>}
          <form onSubmit={handleSetDisplayName}>
            <div className="form-group">
              <label>Display Name</label>
              <input className="input" value={displayName}
                onChange={e => setDisplayName(e.target.value)} required
                placeholder="How others will see you" autoFocus />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading || !displayName.trim()}>
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </form>
          <button type="button" className="btn btn-secondary btn-sm mt-8" style={{ width: '100%' }}
            onClick={() => navigate(redirectTo, { replace: true })}>
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <img src="/logo.svg" alt="Emotional Intelligence" style={{ width: 80, height: 80, margin: '0 auto 16px', display: 'block' }} />
        <h1>Welcome back</h1>
        <p className="subtitle">Sign in to listen and vote</p>

        <div className="tabs" style={{ marginBottom: 16 }}>
          <button className={`tab ${mode === 'email' ? 'active' : ''}`} onClick={() => { setMode('email'); setError(''); }}>
            Email
          </button>
          <button className={`tab ${mode === 'phone' ? 'active' : ''}`} onClick={() => { setMode('phone'); setError(''); setOtpSent(false); }}>
            Phone
          </button>
        </div>

        {error && <div className="error-msg">{error}</div>}

        {mode === 'email' && (
          <form onSubmit={handleEmailLogin}>
            <div className="form-group">
              <label>Email</label>
              <input className="input" type="email" value={email}
                onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input className="input" type="password" value={password}
                onChange={e => setPassword(e.target.value)} required />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {mode === 'phone' && !otpSent && (
          <form onSubmit={handleRequestOtp}>
            <div className="form-group">
              <label>Phone Number</label>
              <input className="input" type="tel" value={phone}
                onChange={e => setPhone(e.target.value)} required
                placeholder="+1 (555) 123-4567" />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
              {loading ? 'Sending...' : 'Send Verification Code'}
            </button>
          </form>
        )}

        {mode === 'phone' && otpSent && (
          <form onSubmit={handleVerifyOtp}>
            <p className="text-secondary text-sm mb-16">
              A verification code was sent to {phone}
            </p>
            <div className="form-group">
              <label>Verification Code</label>
              <input className="input" value={otpCode}
                onChange={e => setOtpCode(e.target.value)} required
                placeholder="Enter code" autoFocus />
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
            <button type="button" className="btn btn-secondary btn-sm mt-8" style={{ width: '100%' }}
              onClick={() => { setOtpSent(false); setOtpCode(''); setError(''); }}>
              Resend code
            </button>
          </form>
        )}

        <p className="mt-16 text-sm text-secondary" style={{ textAlign: 'center' }}>
          Don't have an account? <Link to="/register" state={{ from: redirectTo }}>Create an account</Link>
        </p>
      </div>
    </div>
  );
}
